import { useState } from 'react';
import { useOnboardingStore } from '@/store/onboardingStore';
import { TextureButton } from '@/components/ui/TextureButton';
import { ChipGroup } from '../components/ChipGroup';
import type { TrainingFrequency, TrainingIntensity } from '@/types/onboarding';

const FREQUENCY_OPTIONS: { value: any; label: string; icon: string }[] = [
    { value: 2, label: '2x / Week', icon: '' },
    { value: 3, label: '3x / Week', icon: '' },
    { value: 4, label: '4x / Week', icon: '' },
    { value: 5, label: '5x / Week', icon: '' },
    { value: 6, label: '6x / Week', icon: '' },
    { value: 7, label: '7x / Week', icon: '' },
];

const INTENSITY_OPTIONS: { value: TrainingIntensity; label: string; icon: string }[] = [
    { value: 'moderate', label: 'Moderate', icon: '🟦' },
    { value: 'intense', label: 'Intense', icon: '🟧' },
    { value: 'elite', label: 'Elite', icon: '🟥' },
];

export function TrainingSetupScreen() {
    const { profile, setTrainingSetup, nextStep } = useOnboardingStore();

    const [freq, setFreq] = useState<TrainingFrequency>(profile.trainingFrequency || 3);
    const [intensity, setIntensity] = useState<TrainingIntensity>(profile.trainingIntensity || 'intense');

    const handleContinue = () => {
        setTrainingSetup(freq, intensity);
        nextStep();
    };

    return (
        <div className="px-2 animate-fade-in-right space-y-8">
            <div className="space-y-4">
                <div>
                    <h2 className="text-xl font-bold text-text-primary">Training Setup</h2>
                    <p className="text-text-secondary text-sm">How often can you train?</p>
                </div>

                <ChipGroup
                    options={FREQUENCY_OPTIONS}
                    selected={freq as any} // Cast for compatibility with string-based ChipGroup logic if needed, or update ChipGroup to generic T
                    onChange={(val) => setFreq(Number(val) as TrainingFrequency)}
                    columns={3}
                />
            </div>

            <div className="space-y-4">
                <div>
                    <h3 className="text-sm font-bold text-text-primary uppercase tracking-wider">Intensity</h3>
                    <p className="text-text-secondary text-xs">Be honest about your capacity.</p>
                </div>

                <div className="grid grid-cols-1 gap-3">
                    {INTENSITY_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            onClick={() => setIntensity(opt.value)}
                            className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${intensity === opt.value
                                    ? 'bg-gold-500/10 border-gold-500 text-white'
                                    : 'bg-white/5 border-white/10 text-text-muted hover:bg-white/10'
                                }`}
                        >
                            <span className="text-2xl">{opt.icon}</span>
                            <div className="text-left">
                                <div className="font-bold text-sm">{opt.label}</div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            <TextureButton onClick={handleContinue} className="w-full">
                Next Step
            </TextureButton>
        </div>
    );
}
